import { Chip, FormHelperText, FormLabel, Grid } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import './ListChip.scss';
import { useTranslation } from 'react-i18next';

interface ChipOption {
  label: string;
  enable: boolean;
}

const AppListChip: FC<AppListChipProps> = ({
  label,
  options: initOptions,
  value: initValue,
  onChange,
  isRow = false,
  required,
  error,
}) => {
  const { t } = useTranslation();

  const [options, setOption] = useState<ChipOption[]>([]);
  const [value, setValue] = useState<string>(initValue);

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('message.MSE00002', { 0: t(label!) });
    }

    return error?.message ? t(error?.message) : null;
  }, [error]);

  const onClick = (item: ChipOption) => {
    setOption((prevOptions) =>
      prevOptions.map((opt) =>
        opt.label === item.label ? { ...opt, enable: !opt.enable } : opt,
      ),
    );
  };

  useEffect(() => {
    setOption(
      initOptions
        ? initOptions.map((opt) => ({
            label: opt,
            enable: initValue ? initValue.split(',').includes(opt) : false,
          }))
        : [],
    );
  }, [initOptions, initValue]);

  useEffect(() => {
    const enabledValues = options
      .filter((opt) => opt.enable)
      .map((opt) => opt.label)
      .join(',');
    setValue(enabledValues);
  }, [options]);

  useEffect(() => {
    onChange(value);
  }, [value]);

  return (
    <Grid container className="ListChip" direction={isRow ? 'row' : 'column'}>
      {label && (
        <Grid
          item
          xs={4}
          alignItems="center"
          justifyContent={isRow ? 'flex-end' : 'flex-start'}
        >
          <FormLabel required={required}>{t(label)}</FormLabel>
        </Grid>
      )}
      <Grid
        item
        xs={8}
        sx={{ width: '100%', flexDirection: 'column', paddingTop: 2 }}
      >
        {options.map((item, index) => (
          <Chip
            key={index}
            color={item.enable ? 'primary' : 'default'}
            label={item.label}
            onClick={() => onClick(item)}
          />
        ))}
      </Grid>
      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </Grid>
  );
};

export default AppListChip;
